import { useState } from "react";
import type { CSSProperties } from "react";
import type { SessionConfig } from "../lib/params";
import { sound } from "../lib/sound";
import { stardust } from "./Stardust";

/**
 * MOMENT 6 — the keepsake. A private night sky that grows by one star every
 * time this child finishes a story, and — slowly, over many nights — the stars
 * JOIN INTO CONSTELLATIONS: every five nights completes a little figure (a fox,
 * a boat, a kite…), so the parent watches the sky gain its own mythology.
 * It lives in localStorage, per family/child, and is there for the PARENT's
 * felt sense of continuity — never a points-grind for the child (no score, no
 * streaks; the newest star simply, quietly joins the others).
 *
 * One small ritual: the sky can hold a single wish per night — a tap sends one
 * shooting star across the room (via the stardust layer) with a soft shimmer.
 * No reward, no loop; just wonder.
 */

const KEY_PREFIX = "tella:nights:";
const PER_CONSTELLATION = 5;
const MAX_SHOWN = 30; // the sky fills over a month of nights; the count keeps going

function keyFor(config: SessionConfig | null): string | null {
  if (!config?.family || !config?.child) return null;
  return `${KEY_PREFIX}${config.family}:${config.child}`.toLowerCase();
}

/**
 * Add one star and persist it; returns the new count. Never throws — if storage
 * is unavailable we still return 1 so tonight's star always shows.
 */
export function bumpKeepsake(config: SessionConfig | null): number {
  const key = keyFor(config);
  if (!key || typeof localStorage === "undefined") return 1;
  try {
    const prev = parseInt(localStorage.getItem(key) ?? "0", 10) || 0;
    const next = prev + 1;
    localStorage.setItem(key, String(next));
    return next;
  } catch {
    return 1;
  }
}

/**
 * Constellation figures as point runs (consecutive points are joined once the
 * figure is complete). Units are "sky units" (0..100 wide, 0..70 tall).
 */
const FIGURES: [number, number][][] = [
  // the little fox
  [[0, 9], [4, 1], [8, 7], [13, 2], [17, 10]],
  // the boat
  [[0, 4], [4, 10], [14, 10], [18, 4], [9, 0]],
  // the crescent
  [[0, 2], [5, 7], [11, 9], [17, 7], [20, 1]],
  // the kite
  [[8, 0], [14, 6], [8, 12], [2, 6], [8, 16]],
  // the lantern
  [[0, 12], [0, 4], [7, 0], [14, 4], [14, 12]],
];

/** Deterministic home for each constellation, spread around the sky. */
function anchorFor(c: number): { x: number; y: number } {
  return { x: 8 + ((c * 47) % 60), y: 8 + ((c * 29) % 42) };
}

/** Where star i lives (sky units): the j-th point of its constellation. */
function starAt(i: number): { x: number; y: number } {
  const c = Math.floor(i / PER_CONSTELLATION);
  const j = i % PER_CONSTELLATION;
  const a = anchorFor(c);
  const [dx, dy] = FIGURES[c % FIGURES.length][j];
  return { x: Math.min(94, a.x + dx * 0.9), y: Math.min(64, a.y + dy * 0.9) };
}

export function NightSky({ count }: { count: number }) {
  const [wished, setWished] = useState(false);
  const n = Math.max(1, count);
  const shown = Math.min(n, MAX_SHOWN);
  const newest = shown - 1;
  const completeGroups = Math.floor(shown / PER_CONSTELLATION);

  function makeWish() {
    if (wished) return;
    setWished(true);
    stardust.wish(); // and one across the whole room (no-op under reduced motion)
    sound.wish();
  }

  return (
    <div className="keepsake">
      <button
        type="button"
        className="night-sky"
        onClick={makeWish}
        aria-label={`Your night sky: ${n} ${n === 1 ? "night" : "nights"} of stories. Tap to make a wish.`}
      >
        {completeGroups > 0 && (
          <svg
            className="sky-lines"
            viewBox="0 0 100 70"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {Array.from({ length: completeGroups }, (_, g) => {
              const pts = Array.from({ length: PER_CONSTELLATION }, (_, j) => {
                const p = starAt(g * PER_CONSTELLATION + j);
                return `${p.x},${p.y}`;
              }).join(" ");
              return <polyline key={g} points={pts} />;
            })}
          </svg>
        )}
        {/* The wish falls in the child's OWN sky — a single streak, once. */}
        {wished && <span className="sky-shoot" aria-hidden="true" />}
        {Array.from({ length: shown }, (_, i) => {
          const { x, y } = starAt(i);
          const isNew = i === newest;
          const r = 1.5 + ((i * 7) % 5) * 0.22;
          const style = {
            left: `${x}%`,
            top: `${(y / 70) * 100}%`,
            width: `${r * 2}px`,
            height: `${r * 2}px`,
            animationDelay: isNew ? "0ms" : `${(i % 7) * 420}ms`,
          } as CSSProperties;
          return (
            <span
              key={i}
              className={isNew ? "sky-star sky-star--new" : "sky-star"}
              style={style}
            />
          );
        })}
      </button>
      <p className="sky-caption">
        {n} {n === 1 ? "night" : "nights"} of stories
      </p>
      <p className="sky-wish">
        {wished ? "your wish is on its way ✦" : "tap your sky to make a wish"}
      </p>
    </div>
  );
}
